/**
 * Fatos do produto que o site público afirma: preço, limites, taxas,
 * lembretes, plataformas. Fonte única para páginas, JSON-LD, `llms.txt` e
 * imagens de prévia — nenhum número de produto se escreve à mão fora daqui.
 *
 * Cada fato espelha o código da API (arquivo citado ao lado). Quando a regra
 * lá mudar, este arquivo muda junto e `FACTS_CHECKED_AT` também; o teste
 * `scripts/seo.test.mjs` compara os planos com `api/src/stripe/constants/plans.ts`.
 *
 * Preço "web" é o de reserva do site: o valor vivo vem do Stripe pela API
 * (`/stripe/plans`) e a tela o prefere quando responde.
 */

/** Data da última conferência destes fatos contra o código (AAAA-MM-DD). */
export const FACTS_CHECKED_AT = '2026-10-03';

/** Conta sem plano pago (`api/src/plan-access/plan-access.ts`). */
export const FREE_PLAN = {
  maxClientsPerMonth: 30,
  /** O que a conta grátis tem. */
  includes: [
    'agenda',
    'cadastro de clientes e serviços',
    'cobranças avulsas',
    'painel de quem pagou e quem falta pagar',
    'despesas e lucro do mês',
    'assistente de configuração na criação da conta',
  ],
  /** O que só os planos pagos têm. */
  paidOnly: [
    'lembrete automático pelo WhatsApp',
    'perfil público com agenda para o cliente marcar sozinho',
    'mensalidade, assinatura e contrato com cobrança recorrente',
    'recebimento pelo link (Pix, cartão ou boleto), com taxa por pagamento',
    'assistente no dia a dia',
  ],
} as const;

export interface SoloPlanFact {
  id: 'basic' | 'professional' | 'enterprise';
  name: string;
  /** Preço mensal no site (reserva; o vivo vem do Stripe). */
  webPrice: number;
  maxClientsPerMonth: number;
  maxPortfolioPhotos: number;
  featuredInDiscovery: boolean;
}

/** Planos de quem atende sozinho (`api/src/stripe/constants/plans.ts`). */
export const SOLO_PLANS: SoloPlanFact[] = [
  { id: 'basic', name: 'Prático', webPrice: 29.9, maxClientsPerMonth: 30, maxPortfolioPhotos: 10, featuredInDiscovery: false },
  { id: 'professional', name: 'Pro', webPrice: 49.9, maxClientsPerMonth: 200, maxPortfolioPhotos: 30, featuredInDiscovery: true },
  { id: 'enterprise', name: 'Gestão', webPrice: 97.9, maxClientsPerMonth: 500, maxPortfolioPhotos: 100, featuredInDiscovery: true },
];

/** Acima disso, plano Sob Medida (`plans.ts`, `team-pricing.ts`). */
export const CUSTOM_PLAN = {
  aboveClientsPerMonth: 500,
  aboveProfessionals: 20,
} as const;

/**
 * Plano Equipe, versão semeada (`api/src/database/migrations/1773900038000-TeamPricingVersion.ts`,
 * espelhada em `app/lib/team-pricing.ts`). Editável no admin: a tela usa o
 * valor vivo; aqui fica o de reserva para texto e JSON-LD. Só vendido no site.
 */
export const TEAM_PLAN = {
  orgFee: 29.9,
  perProfessional: 19.9,
  perClientBlock: 7.5,
  clientBlockSize: 50,
  minProfessionals: 2,
  maxProfessionals: 20,
  maxClientsPerMonth: 5000,
  /** 2 profissionais + 1 bloco de 50 clientes. */
  fromPrice: 77.2,
  webOnly: true,
} as const;

/**
 * Recebimento pelo link (`api/src/payment-account/pricing/payment-pricing.constants.ts`
 * e `platform_settings` de produção, conferidos em 2026-10-03).
 */
export const PAYMENT_FEES = {
  pixPercent: 1.19,
  cardPercent: 3.99,
  cardFixed: 0.39,
  boletoFixed: 3.45,
  pixMin: 0.5,
  pixMax: 15000,
  boletoMin: 5,
  boletoMax: 49999.99,
  cardBrands: ['Visa', 'Mastercard'],
  cardInstallments: false,
  pixSettlementBusinessDays: 2,
  cardSettlementDays: 30,
  /** A Controlapag não fica com parte do pagamento. */
  marginPercent: 0,
} as const;

/** Lembretes (`api/src/charge-schedule`, `api/src/schedule/schedule-cron.service.ts`). */
export const REMINDERS = {
  /** Lembrete de cobrança: dias antes do vencimento, mais o dia do vencimento. */
  chargeDaysBeforeOptions: [3, 5],
  chargeCustomDays: true,
  chargeOnDueDate: true,
  /** Nada sai depois do vencimento. */
  chargeAfterDue: false,
  /** Lembrete de horário: um por agendamento, antecedência ajustável. */
  appointmentRemindersPerBooking: 1,
  appointmentDefaultLeadHours: 24,
  /** Prazo para marcar a falta depois do horário (`appointment.service.ts`). */
  noShowMarkWithinDays: 7,
} as const;

/** Onde o app existe hoje. */
export const PLATFORMS = {
  web: true,
  ios: true,
  android: true,
  appStoreUrl: 'https://apps.apple.com/br/app/controlapag/id6776071956',
  playStoreUrl: 'https://play.google.com/store/apps/details?id=com.controlapag.app',
} as const;

/** Empresa (o que é público). CNPJ e razão social entram quando forem informados. */
export const COMPANY = {
  name: 'Controlapag',
  foundingDate: '2025-11-08',
  city: 'São Paulo',
  region: 'SP',
  country: 'BR',
  email: 'controlapag@controlapag.com.br',
  supportHours: 'segunda a sexta, das 9h às 18h',
  supportResponse: 'até 1 dia útil',
  instagram: 'https://instagram.com/controlapag',
  facebook: 'https://www.facebook.com/controlapag',
  linkedin: 'https://www.linkedin.com/company/controlapag',
  wikidata: 'https://www.wikidata.org/wiki/Q141616151',
} as const;

const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

/** "R$ 29,90" (com o espaço que o Intl põe como não separável trocado por espaço comum). */
export const formatBRL = (value: number): string => brl.format(value).replace(/ /g, ' ');

const cheapestSolo = Math.min(...SOLO_PLANS.map((plan) => plan.webPrice));

/** Frases prontas, para todo texto dizer a mesma coisa. */
export const PROSE = {
  fromPrice: `a partir de ${formatBRL(cheapestSolo)} por mês`,
  free: `grátis para até ${FREE_PLAN.maxClientsPerMonth} clientes por mês, com cobranças avulsas`,
  paidFeatures:
    'lembrete no WhatsApp, link de agendamento, mensalidade e recebimento pelo link (com taxa por pagamento) estão nos planos pagos',
  payment:
    'por padrão o cliente paga direto a você e você toca em Confirmar recebimento; nos planos pagos, ele também pode pagar pelo link, com taxa por pagamento',
  pixFee: `${String(PAYMENT_FEES.pixPercent).replace('.', ',')}% no Pix`,
  cardFee: `${String(PAYMENT_FEES.cardPercent).replace('.', ',')}% + ${formatBRL(PAYMENT_FEES.cardFixed)} no cartão`,
  boletoFee: `${formatBRL(PAYMENT_FEES.boletoFixed)} no boleto`,
  platforms: PLATFORMS.android ? 'na web, no iPhone e no Android' : 'na web e no iPhone',
} as const;

/**
 * Padrões que o assistente de configuração sugere por ramo, em horas
 * (`api/src/activity-pack/packs/*.packs.ts`: `minCancelLeadMinutes` e
 * `reminderLeadMinutes`; beleza vale para barbearia, cabeleireiro, manicure e
 * estética). O profissional muda depois. Dentro do prazo para desmarcar, o
 * cliente não cancela nem remarca pelo app (`appointment.service.ts`,
 * `assertCancelWindow`).
 */
export const PACK_DEFAULTS = {
  beleza: { cancelLeadHours: 2, reminderLeadHours: 24 },
  personalTrainer: { cancelLeadHours: 12, reminderLeadHours: 12 },
  professorParticular: { cancelLeadHours: 12, reminderLeadHours: 12 },
  psicologo: { cancelLeadHours: 24, reminderLeadHours: 24 },
  fisioterapeuta: { cancelLeadHours: 24, reminderLeadHours: 24 },
} as const;

/**
 * Cota de sessões da mensalidade (`api/src/schedule/subscription-quota.service.ts`):
 * a falta gasta a sessão, o cancelamento devolve, e o saldo não passa para o
 * período seguinte.
 */
export const QUOTA_RULES = {
  noShowUsesQuota: true,
  cancelReturnsQuota: true,
  rollsOver: false,
} as const;
